import Matter from "matter-js";
import type { GameConfig } from "../config/GameConfig";

export type BubbleState = "Growing" | "Alive" | "Absorbing" | "Meeting" | "Absorbed";

export class Bubble {
  state: BubbleState = "Growing";
  radius: number;
  pointerId: number | null;
  readonly createdAt: number;
  absorbStartedAt = 0;
  meetStartRadius = 0;
  meetGlow = 0;
  /** Lado onde a bola nasceu; usado para absorver se tentar cruzar a linha. */
  readonly bornAbove: boolean;

  constructor(
    public body: Matter.Body,
    radius: number,
    pointerId: number | null,
    createdAt: number,
    bornAbove = true,
  ) {
    this.radius = radius;
    this.pointerId = pointerId;
    this.createdAt = createdAt;
    this.bornAbove = bornAbove;
  }

  get x(): number {
    return this.body.position.x;
  }

  get y(): number {
    return this.body.position.y;
  }

  get id(): number {
    return this.body.id;
  }

  setRadius(next: number): void {
    const clamped = Math.max(0.5, next);
    if (this.state !== "Alive") {
      this.radius = clamped;
      return;
    }
    const current = this.body.circleRadius ?? this.radius;
    if (current < 1e-4) {
      this.radius = clamped;
      return;
    }
    const scale = clamped / current;
    if (Math.abs(scale - 1) < 1e-5) {
      this.radius = clamped;
      return;
    }
    Matter.Body.scale(this.body, scale, scale);
    this.radius = this.body.circleRadius ?? clamped;
  }

  follow(x: number, y: number): void {
    Matter.Body.setPosition(this.body, { x, y });
    Matter.Body.setVelocity(this.body, { x: 0, y: 0 });
  }

  attachLiveBody(body: Matter.Body): void {
    this.body = body;
    this.state = "Alive";
    this.pointerId = null;
  }

  absorb(): void {
    this.state = "Absorbing";
    this.pointerId = null;
    this.absorbStartedAt = performance.now();
    this.body.isSensor = true;
    Matter.Body.setStatic(this.body, true);
    Matter.Body.setVelocity(this.body, { x: 0, y: 0 });
  }

  beginMeeting(): void {
    this.state = "Meeting";
    this.pointerId = null;
    this.meetStartRadius = Math.max(this.radius, 1);
    this.meetGlow = 0;
    this.body.isSensor = true;
    Matter.Body.setStatic(this.body, true);
    Matter.Body.setVelocity(this.body, { x: 0, y: 0 });
  }

  fieldInfluence(config: GameConfig): number {
    const t = 1 - this.radius / config.bubble.maxRadius;
    const feel = config.feel;
    const small = Math.max(0, t);
    return feel.minFieldInfluence + (1 - feel.minFieldInfluence) * (1 - small) ** feel.fieldFalloff;
  }

  attractionInfluence(config: GameConfig): number {
    const t = Math.max(0, 1 - this.radius / config.bubble.maxRadius);
    const feel = config.feel;
    return (
      feel.minAttraction +
      (feel.maxAttraction - feel.minAttraction) * t ** feel.attractionCurve
    );
  }

  area(): number {
    return Math.PI * this.radius * this.radius;
  }
}

export function createBubbleBody(
  x: number,
  y: number,
  radius: number,
  config: GameConfig,
  live: boolean,
): Matter.Body {
  return Matter.Bodies.circle(x, y, radius, {
    isStatic: !live,
    isSensor: !live,
    restitution: config.bubble.restitution,
    friction: 0.02,
    frictionAir: config.bubble.airFriction,
    density: config.bubble.density,
    label: "bubble",
    collisionFilter: {
      category: 0x0001,
      mask: 0x0001 | 0x0002,
    },
  });
}
